// CSS styling for a caption preview, shared by the editor's VideoPlayer and the landing hero.
//
// These are the DOM-side counterparts to assGeneratorService's ASS output: the same design-space
// numbers (fontSize, outline, shadow, padding) turned into CSS instead of ASS tags. They live here
// rather than in a component because both previews need them, and because the landing hero used to
// carry a hand-copied subset that drifted from the editor's version.
//
// Sizes follow the burn's text scale: the height scale, times textScaleFactor for width-sized
// templates (sizeBasis: 'width') on landscape video. Margins and padding use the plain scale.
// Extension included on purpose: this module is imported directly by the Node test suite
// (backend/tests), which resolves ESM strictly and cannot fall back to Vite's extension guessing.
import { textScaleFactor, LIBASS_FONT_COMPENSATION, INDIC_SCRIPTS, indicScript } from './captionLayout.js';

// Preview-side mirror of the burn's Indic font routing (captionLayout.resolveFontFace): which
// bundled family a caption in `script` falls back to for `fontFamily`, or null when the family
// already covers the script. Bangers and any Baloo keep a display look via the script's Baloo
// sibling; everything else falls back to the script's Noto face (see INDIC_SCRIPTS).
const indicFallbackFamily = (fontFamily, script) => {
  const entry = script ? INDIC_SCRIPTS[script] : null;
  if (!entry) return null;
  const fam = (fontFamily || '').toLowerCase();
  if (fam.includes(entry.noto.key) || fam.includes(entry.display.key)) return null;
  return /bangers|baloo/.test(fam) ? entry.display.family : entry.noto.family;
};
// Whether the style's uploaded font (style.customFonts registry, the same list the burn's
// pickCustomFont reads) covers `script`; then neither renderer routes away from it.
const customFontCovers = (style, script) => {
  const fam = (style && typeof style.fontFamily === 'string') ? style.fontFamily.trim().toLowerCase() : '';
  const list = style && Array.isArray(style.customFonts) ? style.customFonts : null;
  const entry = script ? INDIC_SCRIPTS[script] : null;
  if (!fam || !list || !entry) return false;
  return list.some((f) => f && typeof f.family === 'string' && f.family.trim().toLowerCase() === fam && !!f[entry.supportsFlag]);
};

/**
 * Whether this browser paints `paint-order: stroke fill` on HTML text. Chromium honours it from
 * 123 (the CEP panel's Chromium 99 does not), Firefox from 60, but WebKit (Safari, every iOS
 * browser) only applies it to SVG: on HTML the stroke paints OVER the fill, and a thin-stroked
 * Devanagari glyph turns into a dark blob with a white fringe. Node (tests, sync) says yes so
 * the parity snapshots stay the true-stroke CSS. Cached after the first call.
 */
let textPaintOrderSupport = null;
export const supportsTextPaintOrder = () => {
  if (textPaintOrderSupport !== null) return textPaintOrderSupport;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (!ua) return (textPaintOrderSupport = true);
  const chromium = ua.match(/(?:Chrome|Chromium|CriOS)\/(\d+)/);
  const ios = /iPhone|iPad|iPod/.test(ua);
  if (/AppleWebKit/.test(ua) && (ios || !chromium)) return (textPaintOrderSupport = false);
  if (chromium && parseInt(chromium[1], 10) < 123) return (textPaintOrderSupport = false);
  return (textPaintOrderSupport = true);
};

/**
 * Rewrites a text style's WebkitTextStroke (the full 2x width captionTextStyle sets) into a
 * ring of text shadows with the visible outer radius, which is how the burn's \\bord looks and
 * needs no paint-order. The ring goes under any drop shadow or glow already on the style. Step
 * count grows with the radius so wide outlines do not scallop. Styles without a stroke pass
 * through untouched.
 */
export const outlineRingShadows = (radius, color) => {
  if (!(radius > 0)) return [];
  const steps = Math.min(32, Math.max(16, Math.ceil((2 * Math.PI * radius) / 1.25)));
  const ring = [];
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    ring.push(`${(Math.cos(a) * radius).toFixed(2)}px ${(Math.sin(a) * radius).toFixed(2)}px 0 ${color}`);
  }
  return ring;
};
export const strokeToShadowRing = (textStyle) => {
  const m = String(textStyle?.WebkitTextStroke || '').match(/^([\d.]+)px\s+(.+)$/);
  const radius = m ? parseFloat(m[1]) / 2 : 0;
  if (!(radius > 0)) return textStyle;
  const ring = outlineRingShadows(radius, m[2]);
  const existing = textStyle.textShadow && textStyle.textShadow !== 'none' ? `, ${textStyle.textShadow}` : '';
  return { ...textStyle, WebkitTextStroke: '0px transparent', textShadow: `${ring.join(', ')}${existing}` };
};

/** The outline as this browser can actually draw it: a true stroke, or the shadow ring. */
export const withPortableOutline = (textStyle) => (supportsTextPaintOrder() ? textStyle : strokeToShadowRing(textStyle));

/** Applies a 0-100 opacity to a hex colour, returning rgba(). Non-hex input passes through. */
export const hexWithOpacity = (hex, opacity = 100) => {
  if (!hex || opacity >= 100) return hex;
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${(opacity / 100).toFixed(2)})`;
};

// Builds the CSS text-shadow layers for the caption drop shadow at the current render scale.
// Mirrors the burn's shadow-copy math in assGeneratorService: the `shadow` slider historically
// offset the copy by (s, s), magnitude s·√2 at 45°, so the default 45° angle reproduces that look
// exactly and other angles keep the same magnitude. Shared by the caption block and the word spans
// so the two renderings can't drift apart.
export const buildDropShadowLayers = (style, s) => {
  const dist = (style.shadow || 0) * s;
  if (!(dist > 0)) return [];
  const rad = ((parseFloat(style.shadowAngle ?? 45) || 0) * Math.PI) / 180;
  const dx = Math.cos(rad) * dist * Math.SQRT2;
  const dy = Math.sin(rad) * dist * Math.SQRT2;
  const blur = style.shadowBlur != null
    ? Math.max(0, style.shadowBlur * s)
    : Math.max(2, dist * 0.6);
  const color = hexWithOpacity(style.shadowColor || '#000000', style.shadowOpacity ?? 100);
  const layers = [`${dx.toFixed(1)}px ${dy.toFixed(1)}px ${blur.toFixed(1)}px ${color}`];
  const spread = (style.shadowSpread || 0) * s;
  if (spread > 0) {
    // CSS text-shadow has no spread; approximate the dilation with a ring of eight extra
    // copies around the base offset (the burn dilates via \bord).
    const d = spread * Math.SQRT1_2;
    [[spread, 0], [d, d], [0, spread], [-d, d], [-spread, 0], [-d, -d], [0, -spread], [d, -d]]
      .forEach(([ox, oy]) => layers.push(`${(dx + ox).toFixed(1)}px ${(dy + oy).toFixed(1)}px ${blur.toFixed(1)}px ${color}`));
  }
  return layers;
};

/**
 * The caption block's text style: font (with the Indic routing of INDIC_SCRIPTS), size, colour,
 * stroke, shadow, glow and backdrop blur. `videoWidth`/`videoHeight` are the real media dimensions,
 * not the preview's, because textScaleFactor keys off the source aspect ratio. `hasDevanagari`
 * carries the caption script (an INDIC_SCRIPTS id, legacy true = Devanagari).
 */
export const captionTextStyle = (style, scale, {
  videoWidth = 1080,
  videoHeight = 1920,
  hasDevanagari = false,
  customFontSupportsDevanagari = false,
  customFontSupportsBengali = false,
  customFontSupportsTelugu = false,
  customFontSupportsTamil = false,
  customFontSupportsGujarati = false,
  customFontSupportsArabic = false,
} = {}) => {
  const script = indicScript(hasDevanagari);
  const explicitCover = { devanagari: customFontSupportsDevanagari, bengali: customFontSupportsBengali, telugu: customFontSupportsTelugu, tamil: customFontSupportsTamil, gujarati: customFontSupportsGujarati, arabic: customFontSupportsArabic };
  const customCovers = !!(script && explicitCover[script])
    || customFontCovers(style, script);
  const fallbackFamily = customCovers ? null : indicFallbackFamily(style.fontFamily, script);
  const s = scale * textScaleFactor(style, videoWidth, videoHeight);
  const fontSize = style.fontSize * s;
  const outlineVal = (style.outline || 0) * s;
  // No letter spacing for Indic captions: the burn cannot track any Indic script without
  // breaking their shaping (see assGeneratorService finalSpacing), so the preview matches.
  const letterSpacing = script ? 0 : (style.letterSpacing || 0) * s;
  const shadows = buildDropShadowLayers(style, s);

  const base = {
    // Bangers (Tabahi/Mr Beast) keeps a display look via the script's Baloo sibling,
    // mirroring the burn-side routing in assGeneratorService; everything else falls back to Noto.
    fontFamily: fallbackFamily
      ? `'${fallbackFamily}', ${style.fontFamily}, sans-serif`
      : `${style.fontFamily}, sans-serif`,
    fontSize: `${fontSize}px`,
    color: style.color,
    fontWeight: style.fontWeight || (style.bold ? 'bold' : 'normal'),
    fontStyle: style.italic ? 'italic' : 'normal',
    textAlign: style.textAlign,
    lineHeight: style.lineHeight,
    letterSpacing: `${letterSpacing}px`,
    textTransform: style.textTransform || (style.uppercase ? 'uppercase' : 'none'),
    textShadow: shadows.length > 0 ? shadows.join(', ') : 'none',
    // Outline via WebkitTextStroke + paint-order: stroke fill. paint-order paints the fill ON TOP
    // of the stroke, covering its inner half, so only a clean outward ring remains. The stroke is
    // outlineVal*2 wide so the visible outer portion is outlineVal px.
    WebkitTextStroke: outlineVal > 0 ? `${outlineVal * 2}px ${style.outlineColor || '#000000'}` : '0px transparent',
    paintOrder: 'stroke fill',
    whiteSpace: 'pre-line',
    // Backdrop blur only makes sense behind a visible background box; once the background is
    // removed the blur has to go too, or a ghost blurred rectangle remains.
    backdropFilter: (style.backgroundColor && style.backdropFilter) || 'none',
    WebkitBackdropFilter: (style.backgroundColor && style.backdropFilter) || 'none',
  };

  if (style.glowColor) {
    const glow = `0 0 ${(style.glowIntensity || 10) * s}px ${style.glowColor}`;
    base.textShadow = base.textShadow === 'none' ? glow : `${base.textShadow}, ${glow}`;
  }
  return withPortableOutline(base);
};

/**
 * Box chrome for the caption block: padding, per-line pills (boxType 'lines') and the per-word
 * drop shadow. Kept apart from captionTextStyle because the editor draws its own selection
 * outline and drag affordances on the same element.
 */
export const captionBoxGeometry = (style, scale, { videoWidth, videoHeight } = {}) => {
  // line-height leading plus em slack, so the *visible* top/bottom gap equals the horizontal gap.
  // Otherwise a line-height of e.g. 1.4 inflates the box and the vertical padding looks far larger
  // than the sides. Mirrors the tight, even box the burn produces.
  const basePad = (typeof style.padding === 'number' ? style.padding : 10);
  const lh = parseFloat(style.lineHeight) || 1.4;
  const padV = Math.max(2, basePad - (((lh - 1) / 2) + 0.14) * (style.fontSize || 48));
  const isLinesBox = style.boxType === 'lines';
  // boxType 'lines': every wrapped line is its own pill, drawn like the burn's per-line rects, a
  // tight box of glyph height (0.55 of the libass size) plus padding on every side, with pill tops
  // one line height apart. The line's own line-height is set to that glyph box so the CSS
  // background hugs the letters the same way.
  const PILL_GLYPH_BOX = 0.55 * LIBASS_FONT_COMPENSATION;
  const shadowLayers = buildDropShadowLayers(style, scale);
  // Word spans set their own text-shadow (the drop shadow), which replaces the block's. Where the
  // outline is a shadow ring rather than a stroke (see supportsTextPaintOrder) the ring has to
  // ride along in that per-word shadow or the words lose their outline. Same width as the
  // stroke captionTextStyle would have drawn (outline x the text scale).
  const outlineVal = (style.outline || 0) * scale * textScaleFactor(style, videoWidth, videoHeight);
  const ring = supportsTextPaintOrder() ? [] : outlineRingShadows(outlineVal, style.outlineColor || '#000000');

  return {
    isLinesBox,
    outlineRing: ring.join(', '),
    boxPaddingCss: `${padV * scale}px ${basePad * scale}px`,
    linePillStyle: isLinesBox ? {
      backgroundColor: hexWithOpacity(style.backgroundColor, style.backgroundOpacity) || '#000000',
      padding: `${basePad * scale}px`,
      lineHeight: PILL_GLYPH_BOX,
      boxShadow: (style.boxShadowDepth || 0) > 0
        ? `0 ${style.boxShadowDepth * scale}px ${style.boxShadowDepth * 1.5 * scale}px ${hexWithOpacity(style.boxShadowColor || '#000000', style.boxShadowOpacity ?? 40)}`
        : 'none',
      borderRadius: `${(style.borderRadius || 8) * scale}px`,
    } : null,
    // Gap so the next pill's top lands one line height below this one's (design px, then scaled).
    // Negative when the line height is tighter than the pill, same as the burn.
    linePillGap: isLinesBox
      ? (lh * (style.fontSize || 48) - (PILL_GLYPH_BOX * (style.fontSize || 48) + 2 * basePad)) * scale
      : 0,
    wordDropShadow: [...ring, ...shadowLayers].join(', ') || 'none',
  };
};

/**
 * The filled rounded box behind the spoken word (style.activeWordPill). It hugs the glyphs the
 * way the per-line pills do (line box of 0.55 × the libass size, 0.50 × the compensated size for
 * Devanagari faces), so the burn's drawn rect and the preview's span background agree. Pads and radius are design px, like padding/borderRadius,
 * and scale with the preview. The box casts the caption's drop shadow (the burn draws a shadow
 * copy of the rect) and the word inside casts none, since its shadow would fall on the box.
 * null when the style has no pill.
 */
export const ACTIVE_WORD_PILL_DEFAULTS = { padX: 8, padY: 4, radius: 6, angle: 0 };
// The script whose face the preview routes this caption to (an INDIC_SCRIPTS id, or null for
// a Latin caption or a family that already covers the script): the CSS font stack in
// captionTextStyle and the burn's resolveFontFace make the same call.
const pillScript = (style, hasDevanagari) => {
  const script = indicScript(hasDevanagari);
  if (!script || customFontCovers(style, script)) return null;
  return indicFallbackFamily(style?.fontFamily, script) ? script : null;
};
// Line-box centres, size compensation and glyph box (the burn's GLYPH_H for the script, see
// assGeneratorService) of the face a pill word renders in, from the script registry.
const pillFaceMetrics = (style, script) => {
  const entry = INDIC_SCRIPTS[script];
  const family = indicFallbackFamily(style?.fontFamily, script);
  const kind = family === entry.display.family ? 'display' : 'noto';
  const face = entry[kind];
  return { win: face.winCentre, typo: face.typoCentre, comp: face.sizeCompensation, glyphBox: entry.glyphBox, display: kind === 'display' };
};
const inkExtentsFor = (script, word, face) => INDIC_SCRIPTS[script].inkExtents(word, !!(face && face.display));
/**
 * Pixels the pill box (not the word) moves up for this word, 0 when nothing needs to move. CSS
 * centres the inline box on the face's typo metrics; the burn centres its rect on the line box
 * (win metrics) for a Latin word, or on the word's own ink band for a Devanagari word (see
 * devanagariInkExtents), so the box covers the marks above and the matras below. The caller
 * paints the box `lift` higher and the glyphs `lift` lower inside it, so the word keeps its
 * baseline. Negative when the box must drop (a hanging matra with nothing above). Uses the plain
 * fontSize x scale like the pill's own padding; the landscape text scale (sizeBasis 'width') is
 * not applied to pill templates.
 */
export const activeWordPillLift = (style, scale, { hasDevanagari = false, word = '' } = {}) => {
  const script = style && style.activeWordPill ? pillScript(style, hasDevanagari) : null;
  if (!script) return 0;
  const face = pillFaceMetrics(style, script);
  const ink = inkExtentsFor(script, word, face);
  const centre = ink ? (ink.top + ink.bottom) / 2 : face.win;
  return (centre - face.typo) * (style.fontSize || 48) * scale;
};
export const activeWordPillStyle = (style, scale, { hasDevanagari = false, word = '' } = {}) => {
  if (!style || !style.activeWordPill) return null;
  // Devanagari captions are routed to Noto Sans Devanagari (or Baloo 2 for Bangers), which the burn
  // draws at a compensated Fontsize with a 0.50 glyph box (assGeneratorService GLYPH_H); Bengali
  // goes to Noto Sans Bengali / Baloo Da 2 with the Latin 0.55 box and its own compensation, Telugu
  // and Tamil to their Noto / Baloo faces with taller boxes (INDIC_SCRIPTS glyphBox). The
  // CSS font-size carries no such compensation, so the box grows by it here to stay the burn's
  // height. An Indic word gets its own ink band instead (marks above, matras below), like the burn.
  const script = pillScript(style, hasDevanagari);
  const face = script ? pillFaceMetrics(style, script) : null;
  const ink = script ? inkExtentsFor(script, word, face) : null;
  const glyphBox = face ? face.glyphBox * face.comp : 0.55;
  const dim = (v, fallback) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v) : fallback);
  const padX = dim(style.activeWordPillPadX, ACTIVE_WORD_PILL_DEFAULTS.padX);
  const padY = dim(style.activeWordPillPadY, ACTIVE_WORD_PILL_DEFAULTS.padY);
  const radius = dim(style.activeWordPillRadius, ACTIVE_WORD_PILL_DEFAULTS.radius);
  const shadow = buildDropShadowLayers(style, scale);
  return {
    backgroundColor: style.activeWordPillColor || '#000000',
    padding: `${padY * scale}px ${padX * scale}px`,
    borderRadius: `${radius * scale}px`,
    lineHeight: ink ? ink.top - ink.bottom : glyphBox * LIBASS_FONT_COMPENSATION,
    boxShadow: shadow.length ? shadow.join(', ') : 'none',
    textShadow: 'none',
    // The burn draws the pill word with \\bord0: no outline inside the box. Without this the
    // word inherits the caption's stroke and a dark word on a light pill becomes a black blob.
    WebkitTextStroke: '0px transparent',
  };
};

/**
 * Tilt of the spoken word's box, in CSS clockwise degrees (style.activeWordPillAngle): the box
 * and its word turn together while the rest of the line stays level. 0 without a pill or angle.
 */
export const activeWordPillAngle = (style) => {
  if (!style || !style.activeWordPill) return 0;
  const a = parseFloat(style.activeWordPillAngle);
  return Number.isFinite(a) ? a : ACTIVE_WORD_PILL_DEFAULTS.angle;
};

/** Seconds from a caption timestamp, tolerating both "HH:MM:SS.mmm" and a plain number. */
export const toSeconds = (t) => {
  if (typeof t === 'number') return Number.isFinite(t) ? t : 0;
  if (!t) return 0;
  const parts = String(t).replace(',', '.').split(':').map(parseFloat);
  return parts.reduce((acc, p) => acc * 60 + (Number.isFinite(p) ? p : 0), 0);
};

// A positioned word tile uses the same center and label dimensions as the ASS drawing. A spoken
// word is drawn at its activeScale; the box is the label's size at that scale.
export const captionWordTileStyle = (tile, block, px, active = false) => ({
  position: 'absolute', left: `${(tile.x - block.blockLeft) * px}px`,
  top: `${(tile.y - block.blockTop) * px}px`, width: `${tile.width * px}px`,
  height: `${tile.height * px}px`, fontSize: `${tile.cssFontSize * (active ? tile.activeScale : 1) * px}px`,
  transform: `translate(-50%, -50%) rotate(${tile.angle}deg)`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  whiteSpace: 'nowrap', lineHeight: 1, textTransform: 'none',
  ...(active ? { zIndex: 1 } : {}),
});

// Pixels a tile's word moves down in the preview. The burn centres an Indic-routed word on the
// face's win metrics, CSS on its typo metrics (the same offset activeWordPillLift corrects).
export const captionWordTileDrop = (style, tile, px, { hasDevanagari = false, active = false } = {}) => {
  const script = pillScript(style, hasDevanagari);
  if (!script) return 0;
  const face = pillFaceMetrics(style, script);
  return (face.win - face.typo) * tile.cssFontSize * (active ? tile.activeScale : 1) * px;
};
